import React from 'react';
import { connect } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import qsession from '../../core/session/queries';
import DashboardContext from './DasboardContext';
import MenuDrawer from './MenuDrawer';
import RightPanel from './RightPanel';

/**
 * Dashboard
 * =========
 *
 * Layout principal: sidebar à esquerda + conteúdo à direita.
 *
 * Estado de colapso do sidebar (`drawerOpen`):
 *
 * - DESKTOP (≥ lg = 1200px): drawer é PERMANENT.
 *     • `drawerOpen=true`  → expandido (280px), mostra menu completo.
 *     • `drawerOpen=false` → recolhido (72px), mostra só os ícones.
 *     • O `RightPanel` ajusta `marginLeft` pra acompanhar.
 *     • Toggle: chevron flutuante na borda direita do drawer.
 *
 * - MOBILE/TABLET (< lg): drawer é TEMPORARY (overlay).
 *     • `drawerOpen=true`  → drawer visível sobreposto ao conteúdo.
 *     • `drawerOpen=false` → drawer oculto (fora da viewport).
 *     • Toggle: hambúrguer no `ViewAppBar`. Clique fora ou em item fecha.
 *
 * Estado inicial: aberto em desktop, fechado em mobile.
 * Resize: troca de breakpoint força o estado default daquele breakpoint
 * (evita "fiquei no estado errado depois de redimensionar").
 */
function Dashboard(props) {
  const { menu, applet, children, profileData, suspendSession } = props;

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const [drawerOpen, setDrawerOpen] = React.useState(isLargeScreen);

  // Sincroniza com o resize. Ao virar desktop, abre; ao virar mobile, fecha.
  React.useEffect(() => {
    setDrawerOpen(isLargeScreen);
  }, [isLargeScreen]);

  const toggleDrawer = React.useCallback(() => setDrawerOpen((o) => !o), []);
  const closeDrawer = React.useCallback(() => setDrawerOpen(false), []);

  const userProfile = React.useMemo(
    () => ({
      numberOfOwnedOrgs: profileData.get('numOrgsAcesso'),
      organization: profileData.get('orgUltAcesso'),
      apelido: profileData.get('apelido', ''),
      profileType: profileData.get('perfil'),
      avatar: profileData.get('avatar', ''),
      name: profileData.get('nome', ''),
    }),
    [profileData],
  );

  const contextValue = React.useMemo(
    () => ({ toggleDrawer, closeDrawer }),
    [toggleDrawer, closeDrawer],
  );

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexGrow: 1,
        zIndex: 1,
      }}
    >
      <DashboardContext.Provider value={contextValue}>
        <MenuDrawer
          suspendSession={suspendSession}
          onToggleDrawer={toggleDrawer}
          onCloseDrawer={closeDrawer}
          isLargeScreen={isLargeScreen}
          userProfile={userProfile}
          drawerOpen={drawerOpen}
          open={drawerOpen}
          applet={applet}
          menu={menu}
        />
        <RightPanel drawerOpen={drawerOpen} isLargeScreen={isLargeScreen}>
          {children}
        </RightPanel>
      </DashboardContext.Provider>
    </div>
  );
}

function mapStateToProps(state) {
  return {
    profileData: qsession.getUserProfile(state),
  };
}

export default React.memo(connect(mapStateToProps, null)(Dashboard));
